import { calcCarAndBike } from "../../../MotorCalculation/VehiclePremiumCalculator";
import { bike } from "../../../MotorCalculation/configJson";
import { buildPremiumRun, finalizePremium } from "./premiumInputs";
import { coverageOf } from "./companyRules";
import {
  ToastSuccess,
  ToastError,
} from "../../../../UiComponents/Toaster/Toast";

/**
 * Calculate New Two Wheeler Vehicle Full Policy Premium
 * @param {Object} formData - Form data containing policy details
 * @param {Object} selectedMakeModel - Selected vehicle make/model
 * @param {Object} tpData - TP (Third Party) data
 * @param {Function} setOpenLoader - Loader state setter
 * @param {Function} setCalculationResult - Calculation result state setter
 * @param {Function} setShowCalculationResult - Show calculation result state setter
 * @param {Object} [tpVehicle] - Multiple company only: the TP insurer's own vehicle. Own damage is priced from
 *   `selectedMakeModel` (its CC band, IDV, discount, nil dep), third party from `tpVehicle` (its CC's TP rate).
 *   Omitted = one vehicle prices both, exactly as for a single company.
 */
export const calculatePremium = async (
  formData,
  selectedMakeModel,
  tpData,
  setOpenLoader,
  setCalculationResult,
  setShowCalculationResult,
  tpVehicle
) => {
  try {
    setOpenLoader(true);

    // The engine inputs (CC bands, rates, flags) are built in premiumInputs.js —
    // the OD side from the OD vehicle, the TP side from the TP vehicle.
    const { inputData, zoneData, baseRateQuery, tpObj, odCc, tpCc, usedFallback } = buildPremiumRun({
      formData,
      odVehicle: selectedMakeModel,
      tpVehicle,
      tpData,
    });
    if (usedFallback) {
      // 160 is not a neutral default — it sits in the 150-350cc band, so a
      // vehicle with no cc on its master record is priced as a mid-size bike.
      console.warn(
        `[Premium] A vehicle has no usable CC (OD "${selectedMakeModel?.label || selectedMakeModel?.Value || "the selected model"}"` +
        `${tpVehicle ? `, TP "${tpVehicle?.label || tpVehicle?.Value || "the TP model"}"` : ""}) — priced as 160cc. ` +
        "Set the CC on the Make & Model master."
      );
    }

    console.log("=== OnlinePolicy Calculation Debug ===");
    console.log("CC (OD / TP):", odCc, tpCc);
    console.log("IDV:", parseFloat(formData.idv) || 0);
    console.log("Input Data:", inputData);

    var result = await calcCarAndBike(
      inputData,
      zoneData,
      bike,
      baseRateQuery,
      tpObj
    );

    console.log("Calculation Result:", result);
    console.log("Final Premium:", result?.finalPremium);

    // TPPD Restrict is left exactly as calcCarAndBike computed it — already
    // netted into totalBodPremium/netPremiumAB — so Total B, Net Premium and
    // Final Premium match the standalone Calculator page for identical
    // inputs (same engine, no override on top of it here).

    // Zero Depreciation is already calculated in VehiclePremiumCalculator
    // No need to add it again here
    
    // Ensure discount fields are included in result for display
    if (result && typeof result.discountonODPremium !== 'undefined') {
      // discountonODPremium and discountonODPremiumValue are already in result from VehiclePremiumCalculator
      console.log("Discount on OD Premium:", result.discountonODPremium, "%");
      console.log("Discount Value: ₹", result.discountonODPremiumValue);
    }
    
    console.log("=== End OnlinePolicy Calculation Debug ===");

    // Tax and final premium from the engine's own Net Premium [A + B], and which CC each side was priced on.
    finalizePremium(result, { odCc, tpCc, coverage: coverageOf(formData) });
    console.log("Net Premium: ₹", result?.netPremiumAB, "Tax (18%): ₹", result?.tax, "Final Premium: ₹", result?.finalPremium);

    setCalculationResult(result);
    setShowCalculationResult(true);
    setOpenLoader(false);

    // ToastSuccess("New Two Wheeler Vehicle Premium calculated successfully!");

    return result;
  } catch (error) {
    console.error("Calculation error:", error);
    setOpenLoader(false);
    ToastError("Error calculating premium. Please try again.");
    throw error;
  }
};

/**
 * Filter vehicle data based on search input
 * @param {String} searchTerm - Search term
 * @param {Array} vehicleData - Full vehicle data array
 * @returns {Array} Filtered vehicle data
 */
export const filterVehicleData = (searchTerm, vehicleData) => {
  if (!searchTerm) {
    return vehicleData;
  }

  return vehicleData.filter(
    (vehicle) =>
      vehicle.VehicleMake?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.VehicleModel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.Value?.toLowerCase().includes(searchTerm.toLowerCase())
  );
};
